/**
 * Version Check Service
 * 
 * Globally accessible service to check if a new version of the admin app is available.
 * Polls /version.json from the server and compares with the build-time version.
 * 
 * Usage:
 *   import { versionCheck } from '../utils/versionCheck';
 *   const result = await versionCheck.check();
 *   // or force a fresh check:
 *   const result = await versionCheck.check(true);
 */

// Check interval: 2 hours in milliseconds
const CHECK_INTERVAL_MS = 2 * 60 * 60 * 1000;

// Storage key for persisting last check time (sessionStorage so each tab checks independently)
const STORAGE_KEY = 'versionCheck.lastChecked';
const STORAGE_DATA_KEY = 'versionCheck.serverVersion';

export interface ServerVersionInfo {
  version: string;
  environment: string;
  deployedAt: string;
}

export interface VersionCheckResult {
  isNewVersionAvailable: boolean;
  clientVersion: string | undefined;
  serverVersion: ServerVersionInfo | null;
  lastChecked: Date | null;
  error: string | null;
}

// Module-level state (persists across component mounts)
let lastChecked: Date | null = null;
let serverVersion: ServerVersionInfo | null = null;
let lastError: string | null = null;
let checkPromise: Promise<VersionCheckResult> | null = null;

// Get client version from build-time env
const getClientVersion = (): string | undefined => {
  return import.meta.env.VITE_APP_VERSION;
};

// Initialize from sessionStorage on module load
const initFromStorage = () => {
  try {
    const storedTime = sessionStorage.getItem(STORAGE_KEY);
    const storedData = sessionStorage.getItem(STORAGE_DATA_KEY);
    
    if (storedTime) {
      lastChecked = new Date(parseInt(storedTime, 10));
    }
    if (storedData) {
      serverVersion = JSON.parse(storedData);
    }
  } catch (e) {
    // Ignore storage errors
  }
};

// Save to sessionStorage
const saveToStorage = () => {
  try {
    if (lastChecked) {
      sessionStorage.setItem(STORAGE_KEY, lastChecked.getTime().toString());
    }
    if (serverVersion) {
      sessionStorage.setItem(STORAGE_DATA_KEY, JSON.stringify(serverVersion));
    }
  } catch (e) {
    // Ignore storage errors
  }
};

// Check if we should perform a new check
const shouldCheck = (): boolean => {
  if (!lastChecked) return true;
  const elapsed = Date.now() - lastChecked.getTime();
  return elapsed >= CHECK_INTERVAL_MS;
};

// Fetch version.json from server
const fetchServerVersion = async (): Promise<ServerVersionInfo> => {
  // Cache-bust to ensure fresh response
  const response = await fetch(`/version.json?t=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache'
    }
  });
  
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  
  return response.json();
};

// Compare versions
const compareVersions = (clientVer: string | undefined, serverVer: ServerVersionInfo | null): boolean => {
  if (!clientVer || !serverVer) return false;
  return clientVer !== serverVer.version;
};

/**
 * Check for new version
 * @param force - If true, bypasses the 2-hour cache and fetches fresh data
 * @returns VersionCheckResult with comparison data
 */
const check = async (force = false): Promise<VersionCheckResult> => {
  const clientVersion = getClientVersion();
  
  // If we're already checking, return the existing promise
  if (checkPromise) {
    return checkPromise;
  }
  
  // If not forcing and we've checked recently, return cached result
  if (!force && !shouldCheck() && serverVersion) {
    return {
      isNewVersionAvailable: compareVersions(clientVersion, serverVersion),
      clientVersion,
      serverVersion,
      lastChecked,
      error: lastError
    };
  }
  
  // Perform the check
  checkPromise = (async () => {
    try {
      serverVersion = await fetchServerVersion();
      lastChecked = new Date();
      lastError = null;
      saveToStorage();
      
      return {
        isNewVersionAvailable: compareVersions(clientVersion, serverVersion),
        clientVersion,
        serverVersion,
        lastChecked,
        error: null
      };
    } catch (error) {
      lastError = error instanceof Error ? error.message : 'Unknown error';
      
      // Still return cached data if available
      return {
        isNewVersionAvailable: compareVersions(clientVersion, serverVersion),
        clientVersion,
        serverVersion,
        lastChecked,
        error: lastError
      };
    } finally {
      checkPromise = null;
    }
  })();
  
  return checkPromise;
};

/**
 * Get current state without triggering a check
 */
const getState = (): VersionCheckResult => {
  const clientVersion = getClientVersion();
  return {
    isNewVersionAvailable: compareVersions(clientVersion, serverVersion),
    clientVersion,
    serverVersion,
    lastChecked,
    error: lastError
  };
};

/**
 * Clear cached data (useful for testing)
 */
const clearCache = () => {
  lastChecked = null;
  serverVersion = null;
  lastError = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_DATA_KEY);
  } catch (e) {
    // Ignore
  }
};

/**
 * Get the check interval in milliseconds
 */
const getCheckInterval = (): number => CHECK_INTERVAL_MS;

// Initialize on module load
initFromStorage();

// Export as a single object for easy importing
export const versionCheck = {
  check,
  getState,
  clearCache,
  getCheckInterval
};

export default versionCheck;
